import { Controller } from "@hotwired/stimulus"

/**
 * Slide Drawer Controller
 *
 * Manages slide-out drawer panels for inline editing without page navigation.
 * Based on PatternFly/Adobe Commerce drawer patterns.
 *
 * Usage:
 * - Add data-controller="slide-drawer" to the drawer container
 * - Use data-action="click->slide-drawer#open" on trigger buttons
 * - The drawer ID can be passed via data-drawer-id attribute on triggers
 */
export default class extends Controller {
  static targets = ["backdrop", "panel", "content"]
  static values = {
    openClass: { type: String, default: "translate-x-0" },
    closedClass: { type: String, default: "translate-x-full" }
  }

  connect() {
    // Close on escape key
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundHandleKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundHandleKeydown)
    this.unlockScroll()
  }

  open(event) {
    if (event) event.preventDefault()

    // Show the drawer container
    this.element.classList.remove("hidden")

    // Trigger reflow for animation
    this.element.offsetHeight

    // Animate in
    requestAnimationFrame(() => {
      if (this.hasBackdropTarget) {
        this.backdropTarget.classList.remove("opacity-0")
        this.backdropTarget.classList.add("opacity-100")
      }
      if (this.hasPanelTarget) {
        this.panelTarget.classList.remove(this.closedClassValue)
        this.panelTarget.classList.add(this.openClassValue)
      }
    })

    // Lock body scroll
    this.lockScroll()

    // Focus first input
    setTimeout(() => {
      const firstInput = this.element.querySelector("input:not([type='hidden']), select, textarea")
      if (firstInput) firstInput.focus()
    }, 300)
  }

  close(event) {
    if (event) event.preventDefault()

    // Animate out
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove("opacity-100")
      this.backdropTarget.classList.add("opacity-0")
    }
    if (this.hasPanelTarget) {
      this.panelTarget.classList.remove(this.openClassValue)
      this.panelTarget.classList.add(this.closedClassValue)
    }

    // Hide after animation
    setTimeout(() => {
      this.element.classList.add("hidden")
    }, 300)

    // Unlock body scroll
    this.unlockScroll()

    // Dispatch close event
    this.dispatch("closed")
  }

  handleKeydown(event) {
    if (event.key === "Escape" && !this.element.classList.contains("hidden")) {
      this.close()
    }
  }

  lockScroll() {
    document.body.style.overflow = "hidden"
    document.body.style.paddingRight = `${window.innerWidth - document.documentElement.clientWidth}px`
  }

  unlockScroll() {
    document.body.style.overflow = ""
    document.body.style.paddingRight = ""
  }

  // Method to update content dynamically (for AJAX loaded forms)
  updateContent(html) {
    if (this.hasContentTarget) {
      this.contentTarget.innerHTML = html
    }
  }
}
